<template>
	<v-chart
		ref="chartRef"
		:option="option"
		:update-options="updateOptions"
		:autoresize="true"
		:style="{ height: height + 'px' }"
		@datazoom="onDataZoom"
	/>
</template>

<script setup>
import { computed, ref } from "vue";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent, DataZoomComponent } from "echarts/components";
import VChart from "vue-echarts";
import { useAllSideNames } from "../composables/useSideName.js";

use([CanvasRenderer, LineChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent]);

var props = defineProps({
	samples: { type: Array, default: () => [] },
	side: { type: String, default: null }, // "left" | "right" | null (both)
	height: { type: Number, default: 360 },
});

var ZOOM_KEY = "bed.history.zoom";
// Setpoint = success green and current = warning amber match the chip palette
// in the History decisions table ("Automation"=success, "External"=warning), so
// the chart's vocabulary lines up with the rest of the app. Target keeps the
// side color (blue/pink) so the side identity stays visible at a glance.
var COLORS = {
	left: "#42a5f5",
	right: "#ec407a",
	room: "#9e9e9e",
	setpoint: "#66bb6a",
	current: "#ffa726",
};

var names = useAllSideNames();
var chartRef = ref(null);

function loadZoom() {
	try {
		var raw = localStorage.getItem(ZOOM_KEY);
		if (!raw) { return { start: 0, end: 100 }; }
		var p = JSON.parse(raw);
		if (typeof p?.start === "number" && typeof p?.end === "number") { return p; }
	} catch (e) { /* ignore */ }
	return { start: 0, end: 100 };
}

var currentZoom = ref(loadZoom());

function onDataZoom(e) {
	var z = (e?.batch && e.batch[0]) || e;
	if (z == null || z.start == null || z.end == null) { return; }
	currentZoom.value = { start: z.start, end: z.end };
	try { localStorage.setItem(ZOOM_KEY, JSON.stringify(currentZoom.value)); } catch (err) { /* ignore */ }
}

// Merge updates element-wise so the user's dataZoom state is preserved across refreshes.
// `replaceMerge: ['series']` is needed because the series array length changes when the
// side selector toggles (5 series both ↔ 3 series single). `animation: false` (set in
// the option below) keeps these replacements from triggering a re-animation on each poll.
var updateOptions = { notMerge: false, replaceMerge: ["series"] };

function bySide(samples, side) {
	return samples.filter((s) => s.side === side);
}

var option = computed(() => {
	var pts = props.samples ?? [];
	var room = pts
		.filter((s) => s.side === "left" && s.roomTemp != null)
		.map((s) => [s.ts, s.roomTemp]);

	// Side selector forces a single side; the both-sides path is kept defensively
	// (skips the target line to keep 4 lines instead of 7 if it ever fires).
	var sides = props.side === "left" || props.side === "right" ? [props.side] : ["left", "right"];
	var singleSide = sides.length === 1;

	var sideSeries = [];
	var legendData = ["Room"];
	for (var sd of sides) {
		var label = names.value[sd];
		var sideColor = COLORS[sd];
		var rows = bySide(pts, sd);
		var spData = rows.map((s) => [s.ts, s.setpoint]);
		var currentData = rows.map((s) => [s.ts, s.current]);

		if (singleSide) {
			var targetData = rows.map((s) => [s.ts, s.target]);
			legendData.push("Target");
			sideSeries.push({
				name: "Target",
				type: "line",
				showSymbol: false,
				step: "end",
				data: targetData,
				lineStyle: { color: sideColor, width: 1.5, type: "dashed" },
				itemStyle: { color: sideColor },
			});
		}

		var spColor = singleSide ? COLORS.setpoint : sideColor;
		legendData.push(singleSide ? "Setpoint" : label + " setpoint");
		sideSeries.push({
			name: singleSide ? "Setpoint" : label + " setpoint",
			type: "line",
			showSymbol: false,
			step: "end",
			data: spData,
			lineStyle: { color: spColor, width: 2.5, type: "solid" },
			itemStyle: { color: spColor },
		});

		var curColor = singleSide ? COLORS.current : sideColor;
		legendData.push(singleSide ? "Current" : label + " current");
		sideSeries.push({
			name: singleSide ? "Current" : label + " current",
			type: "line",
			showSymbol: false,
			smooth: true,
			data: currentData,
			lineStyle: { color: curColor, width: 1.5, type: singleSide ? "solid" : "dashed" },
			itemStyle: { color: curColor },
		});
	}

	var z = currentZoom.value;

	return {
		darkMode: true,
		backgroundColor: "transparent",
		animation: false,
		tooltip: {
			trigger: "axis",
			formatter: function (params) {
				var when = new Date(params[0].value[0]).toLocaleString();
				var lines = params.map((p) => p.marker + " " + p.seriesName + ": " + (p.value[1] == null ? "—" : Math.round(Number(p.value[1]))) + "°F");
				return when + "<br/>" + lines.join("<br/>");
			},
		},
		legend: {
			data: legendData,
			textStyle: { color: "#ddd" },
			top: 0,
		},
		grid: { left: 40, right: 16, top: 56, bottom: 28 },
		xAxis: {
			type: "time",
			axisLabel: {
				color: "#aaa",
				hideOverlap: true,
				// Default formatter strips to just "HH" at certain zoom levels, which
				// becomes ambiguous across midnight (you see "20, 9, 4"). This per-unit
				// format keeps am/pm on hour ticks and switches to M/d on day ticks.
				formatter: {
					year: "{yyyy}",
					month: "{MMM}",
					day: "{M}/{d}",
					hour: "{h}{a}",
					minute: "{h}:{mm}{a}",
					second: "{H}:{mm}:{ss}",
					millisecond: "{HH}:{mm}:{ss} {SSS}",
					none: "{M}/{d} {h}{a}",
				},
			},
		},
		yAxis: {
			type: "value",
			scale: true,
			name: "°F",
			axisLabel: { color: "#aaa" },
			splitLine: { lineStyle: { color: "#333" } },
		},
		// Pinch/scroll zoom inside the plot is kept; the bottom slider was removed
		// (took ~24px of screen) so use trackpad/touch to zoom and pan.
		dataZoom: [
			{ type: "inside", start: z.start, end: z.end },
		],
		series: [
			{
				name: "Room",
				type: "line",
				showSymbol: false,
				smooth: true,
				data: room,
				lineStyle: { color: COLORS.room, width: 1, type: "dotted" },
				itemStyle: { color: COLORS.room },
			},
			...sideSeries,
		],
	};
});
</script>
